/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

/**
 * Defines the individually observed network connect status event types.
 */
export enum NetworkConnectionStatus {
  online = 'online',
  offline = 'offline',
}

/**
 * Defines the Observer callback function which is used for observing either
 * `NetworkConnectionStatus.online` and / or `NetworkConnectionStatus.offline`
 * events.
 */
export type Observer = (isConnect: boolean, evt?: Event) => void;

/**
 * Defines the Observable collection of registered online and offline event callback.
 */
export type Observables = {
  [NetworkConnectionStatus.offline]: Observer[]
  [NetworkConnectionStatus.online]: Observer[]
}

/**
 * Defines the `Observer` comparator function which is used to determine if a given
 * `Observer` exists.
 */
export type Comparator = (handler: Observer) => boolean;

/**
 * The `NetworkStatus` API provides a centralized point of access for observing changes
 * to the network conditions under which applications are operating, primarily, `online`
 * and `offline` status changes.
 *
 * ```typescript
 *
 *   // add offline and online observers
 *   connection.addObservers(onDisconnect, onConnected);
 *
 *   ...
 *   // add an online observer ...
 *   connection.addOnlineObserver(onReConnected);
 *   connection.hasOnlineObserver(onReConnected); // true
 *
 *   ...
 *   // add an offline observer ...
 *   connection.addOfflineObserver(onConnectionDropped);
 *   connection.hasOfflineObserver(onConnectionDropped); // true
 *
 *   // add a single observer for both offline and online events ...
 *   connection.addObservers(onChange, null, true);
 *
 *   ...
 *   // remove an online observer ...
 *   connection.removeOnlineObserver(onReConnected);
 *   connection.hasOnlineObserver(onReConnected); // false
 *
 *   ...
 *   // remove an offline observer ...
 *   connection.removeOfflineObserver(onConnectionDropped);
 *   connection.hasOfflineObserver(onConnectionDropped); // false
 *
 *   // determine if the application is currently online or offline ...
 *   connection.isConnected; // true if online, otherwise, false
 * ```
 *
 */
export interface NetworkStatus {
  /**
   * Returns the current network connection status based on the underlying browser network
   * events.
   *
   * ```typescript
   *   // determine if the application is currently online or offline ...
   *   connection.isConnected; // true if online, otherwise, false
   * ```
   *
   * @return True if the application is currently online, otherwise, false.
   */
  get isConnected(): boolean;

  /**
   * Provides a convenience utility for determining if a given `Observer` has been added.
   *
   * ```typescript
   *   connection.addOfflineObserver(onDisconnect);
   *   connection.hasObserver(onDisconnect); // true
   *   ...
   *   connection.removeOfflineObserver(onDisconnect);
   *   connection.hasObserver(onDisconnect); // false
   * ```
   *
   * @param   observer The `Observer` instance that is to be validated.
   * @param   type An optional `NetworkConnectionStatus` type from which to validate,
   * @returns True if the observer has been added, otherwise false.
   */
  hasObserver(observer: Observer, type?: NetworkConnectionStatus): boolean;

  /**
   * Provides a convenience utility for determining if a given offline `Observer` has
   * been added.
   *
   * ```typescript
   *   connection.addOnlineObserver(onConnect);
   *   connection.hasOnlineObserver(onConnect); // true
   *   ...
   *   connection.removeOnlineObserver(onConnect);
   *   connection.hasOnlineObserver(onConnect); // false
   * ```
   *
   * @param   observer The online `Observer` instance that is to be validated.
   * @returns True if the observer has been added, otherwise false.
   */
  hasOnlineObserver(observer: Observer): boolean;

  /**
   * Provides a convenience utility for determining if a given offline `Observer` has
   * been added.
   *
   * ```typescript
   *   connection.addOfflineObserver(onDisconnect);
   *   connection.hasOfflineObserver(onDisconnect); // true
   *   ...
   *   connection.removeOfflineObserver(onDisconnect);
   *   connection.hasOfflineObserver(onDisconnect); // false
   * ```
   *
   * @param   observer The offline `Observer` instance that is to be validated.
   * @returns True if the observer has been added, otherwise false.
   */
  hasOfflineObserver(observer: Observer): boolean;

  /**
   * The `addObservers` method allows for adding individual `offline` and `online` network
   * event observers, or a single observer for both network states. In either case, the
   * provided observers will be invoked when the application goes online and / or offline.
   *
   * ```typescript
   *
   *   // add offline and online observers ...
   *   connection.addObservers(onDisconnect, onConnected);
   *
   *   // add a single observer for both offline and online events ...
   *   connection.addObservers(onChange, null, true);
   *
   * ```
   *
   * @param   onDisconnect An `Observer` which is to be invoked when the application
   *          goes offline.
   * @param   onConnect An `Observer` which is to be invoked when the application
   *          goes online.
   * @param   observeAll When true, a single observer will be used for both the
   *          offline and online states. When this argument is set to true, the
   *          first argument (`onDisconnect`) observer will be used for both
   *          network connection states.
   */
  addObservers(onDisconnect: Observer, onConnect: Observer, observeAll?: boolean): void;

  /**
   * The `addOnlineObserver` method allows for adding an `online` network event observer,
   * which will be invoked when the application goes online.
   *
   * ```typescript
   *   // add an online network event observers ...
   *   connection.addOnlineObserver(onConnected);
   * ```
   *
   * @param observer An `Observer` which is to be invoked when the application goes online.
   */
  addOnlineObserver(observer: Observer): void;

  /**
   * The `addOfflineObserver` method allows for adding an `offline` network event observer,
   * which will be invoked when the application goes offline.
   *
   * ```typescript
   *   // add an offline network event observers ...
   *   connection.addOfflineObserver(onDisconnect);
   * ```
   *
   * @param observer An `Observer` which is to be invoked when the application goes offline.
   */
  addOfflineObserver(observer: Observer): void;

  /**
   * The `removeOnlineObserver` method allows for removing a previously added `online`
   * network event observer.
   *
   * ```typescript
   *   // add an online network event observer ...
   *   connection.addOfflineObserver(onConnect);
   *   ...
   *   // remove the previously added online network event observer ...
   *   connection.removeOnlineObserver(onConnect);
   * ```
   *
   * @param observer The `Observer` which is to be removed from observed online events.
   */
  removeOnlineObserver(observer: Observer): void;

  /**
   * The `removeOfflineObserver` method allows for removing a previously added `offline`
   * network event observer.
   *
   * ```typescript
   *   // add an offline network event observer ...
   *   connection.addOfflineObserver(onDisconnect);
   *   ...
   *   // remove the previously added offline network event observer ...
   *   connection.removeOfflineObserver(onDisconnect);
   * ```
   *
   * @param observer The `Observer` which is to be removed from observed offline events.
   */
  removeOfflineObserver(observer: Observer): void;

  /**
   * Removes all network status change event listeners and resets all observers and
   * nullifies the `ConnectionStatus` instance.
   */
  destruct(): void;
}
